/**
 * Staff punch (G6/G12) — gate code in, IN/OUT out. First punch of the day is
 * IN, next is OUT, another reopens (re-entry).
 */
import { router } from 'expo-router';
import React from 'react';
import { Image, View } from 'react-native';

import { space } from '@bero/tokens';
import { str } from '@/components/gate/api';
import { Icon } from '@/components/gate/icons';
import { useT } from '@/i18n';
import { api } from '@/lib/client';
import { useTheme } from '@/theme';
import { AppText, Button, Card, Field, Input, Screen, TopBar } from '@/ui';

type PunchResult = { action: 'in' | 'out'; staff: Record<string, unknown>; at: string };

export default function GuardPunch() {
  const { colors } = useTheme();
  const { t } = useT();
  const [code, setCode] = React.useState('');
  const [result, setResult] = React.useState<PunchResult | null>(null);
  const [error, setError] = React.useState<string | null>(null);
  const [busy, setBusy] = React.useState(false);

  const punch = async () => {
    if (code.trim().length < 4 || busy) return;
    setBusy(true);
    setError(null);
    setResult(null);
    try {
      const body = await api.post<PunchResult>('/api/gate/staff-punch', { code: code.trim() });
      setResult(body);
      setCode('');
    } catch (e) {
      const status = (e as { status?: number }).status;
      setError(status === 404 ? t('punch_not_found') : t('punch_error'));
    } finally {
      setBusy(false);
    }
  };

  const at = result ? new Date(result.at) : null;
  const clock = at && !Number.isNaN(at.getTime())
    ? `${at.getHours() % 12 || 12}:${String(at.getMinutes()).padStart(2, '0')} ${at.getHours() < 12 ? 'AM' : 'PM'}`
    : '';
  const photo = result ? str(result.staff, 'photo') : undefined;

  return (
    <Screen scroll={false}>
      <TopBar title={t('staff_punch')} onBack={() => router.back()} />
      <Screen padded style={{ paddingTop: space.s4, gap: space.s4 }}>
        <Field label={t('punch_code_label')} help={t('punch_code_help')}>
          <Input
            value={code}
            onChangeText={(v) => setCode(v.replace(/[^0-9]/g, '').slice(0, 6))}
            placeholder="7763"
            keyboardType="numeric"
            style={{ fontSize: 24, letterSpacing: 8, textAlign: 'center', minHeight: 56 }}
          />
        </Field>
        <Button title={t('punch_action')} size="lg" loading={busy} disabled={code.length < 4} onPress={() => void punch()} />
        {error ? <AppText variant="bodySm" tone="danger">{error}</AppText> : null}

        {result ? (
          <Card style={{ flexDirection: 'row', alignItems: 'center', gap: space.s3 }}>
            {photo ? (
              <Image source={{ uri: photo }} style={{ width: 52, height: 60, borderTopLeftRadius: 26, borderTopRightRadius: 26, borderBottomLeftRadius: 6, borderBottomRightRadius: 6, backgroundColor: colors.canvas2 }} />
            ) : (
              <Icon name="users" size={28} color={colors.textSecondary} />
            )}
            <View style={{ flex: 1, minWidth: 0 }}>
              <AppText variant="bodyMd" weight="600">{str(result.staff, 'name') ?? 'Staff member'}</AppText>
              <AppText variant="bodySm" tone="tertiary" style={{ marginTop: 2 }}>
                {str(result.staff, 'role') ?? ''}
              </AppText>
            </View>
            <View style={{ alignItems: 'flex-end' }}>
              <AppText variant="bodyMd" weight="700" style={{ color: result.action === 'in' ? colors.success700 : colors.textSecondary }}>
                {result.action === 'in' ? t('punch_checked_in') : t('punch_checked_out')}
              </AppText>
              <AppText variant="bodySm" tone="tertiary" mono>{clock}</AppText>
            </View>
          </Card>
        ) : null}
      </Screen>
    </Screen>
  );
}
